import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createDraw, uploadImage, updateDraw, getDrawInfoById } from '../../api/draws';
import { PATHS } from '../../routes/routes';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { currency as currencyOptions, drawType, timeZone, discountTypes } from '../../@types/darw';
import Tooltip from '../../components/tooltip/Tooltip';
import Alert from '../../components/alert/Alert';
// import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { fetchTerms } from '../../api/terms';
import { FaPen, FaRegFileLines, FaTriangleExclamation, FaTrash, FaPlus, FaXmark } from 'react-icons/fa6';
import { HOST_API } from '../../api/config';
import { validatePromoPeriods, getNowInTimezone } from '../../utils/validation';

const calculateEarnings = (entryCost: number) => {
    const participants = 10000;
    const grossRevenue = Math.round(entryCost * participants);
    const platformFee = Math.round(grossRevenue * 0.15);
    const netEarnings = grossRevenue - platformFee;

    return {
        participants,
        grossRevenue: grossRevenue.toLocaleString(),
        netEarnings: netEarnings.toLocaleString()
    };
};

const LoadingOverlay = () => {
    return (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-white bg-opacity-10 backdrop-blur-[1px] z-50">
            <span className="loading loading-ring loading-lg"></span>
            <p className="pt-1 font-bold text-primary">Uploading image ...</p>
        </div>
    );
};

const schema = z.object({
    name: z.string().nonempty('Draw Name is required'),
    runAt: z.string().nonempty('Date is required'),
    prize: z.string().nonempty('Prize is required'),
    entryCost: z
        .number({
            invalid_type_error: 'Entry Cost must be a valid number'
        })
        .min(1, 'Entry Cost must be at least 1')
        .refine((value) => !Number.isNaN(value), {
            message: 'Please enter a valid Entry Cost'
        }),
    runMethod: z.string().nonempty('Draw Type is required'),
    imageId: z.string().nullable().optional(),
    timezone: z.string().nonempty('Required'),
    currency: z.string().nonempty('Required'),
    discounts: z.array(z.object({
        type: z.string(),
        code: z.string(),
        label: z.string().optional()
    })).optional(),
    ticketCap: z.union([
        z.number().int().nonnegative(),
        z.string().transform((val) => {
            if (val === '' || val === null || val === undefined) return undefined;
            const num = Number(val);
            return Number.isNaN(num) ? undefined : num;
        }),
        z.null(),
        z.undefined()
    ]).optional(),
    numberOfEntries: z
        .number({ invalid_type_error: 'Number of Entries must be a number' })
        .int('Must be an integer')
        .positive('Must be at least 1')
        .default(1),
    emailWinner: z.boolean().optional(),
    // numbers settings are auto-calculated by backend based on ticket cap
});

// Update validation schema
const validationSchema = schema.superRefine((data, ctx) => {
    const currentDiscount = data.discounts?.[0];
    if (currentDiscount?.type && !currentDiscount?.code) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Code or link is required when discount type is selected',
            path: ['discounts', 0, 'code']
        });
    }
    
    // Validate affiliate link format
    if (currentDiscount?.type === 'affiliate' && currentDiscount?.code) {
        try {
            new URL(currentDiscount.code);
        } catch (e) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please enter a valid URL for affiliate link',
                path: ['discounts', 0, 'code']
            });
        }
    }
});

type FormData = z.infer<typeof validationSchema>;

// Add this type definition

const CreateDraw = () => {
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [imgLoading, setImgLoading] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [isInactive, setIsInactive] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [linkWarning, setLinkWarning] = useState('');
    const today = new Date().toISOString().slice(0, 16);
    const navigate = useNavigate();
    const { drawId } = useParams<{ drawId: string }>();
    // Add state for discount codes - like package
    const [drawData, setDrawData] = useState<{ discounts: Array<{ type: string; code: string; label?: string }> }>({
        discounts: []
    });

    // Draw Links Management Section state
    const [links, setLinks] = useState<Array<{
        id?: number;
        name: string;
        slug: string;
        entryCost?: number;
        numberOfEntries?: number;
    }>>([]);
    const [linkForm, setLinkForm] = useState<{
        name?: string;
        slug?: string;
        entryCost?: number;
        numberOfEntries?: number;
    }>({});

    const handleAddLink = () => {
        console.log('handleAddLink called', { linkForm, links });
        if (!linkForm.name || !linkForm.slug) {
            console.log('Missing name or slug', { name: linkForm.name, slug: linkForm.slug });
            return;
        }
        
        // Validate slug format matches backend requirements
        if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(linkForm.slug)) {
            alert('Slug must contain only lowercase letters, numbers, and hyphens (no leading/trailing hyphens)');
            return;
        }
        
    // Check for duplicate slugs
    if (links.some(link => link.slug === linkForm.slug)) {
            alert('A link with this slug already exists. Please use a different slug.');
            return;
        }
        
        setLinks(prev => {
            const newLinks = [
                ...prev,
                {
                    name: linkForm.name as string,
                    slug: linkForm.slug as string,
                    entryCost: linkForm.entryCost,
                    numberOfEntries: linkForm.numberOfEntries
                }
            ];
            console.log('Setting new links', newLinks);
            return newLinks;
        });
        setLinkForm({});
    };

    const handleRemoveLink = (idx: number) => {
        // Always allow removing marketing links, even if draw is locked
        console.log('handleRemoveLink called', { idx, links, isLocked });
        setLinks(prev => {
            const newLinks = prev.filter((_, i) => i !== idx);
            console.log('Setting new links after removal', newLinks);
            return [...newLinks];
        });
    };
    
    // Use ref to store discounts for reliable access
    const discountsRef = useRef<Array<{ type: string; code: string; label?: string }>>([]);
    
    // Initialize discountsRef when drawData changes
    useEffect(() => {
        discountsRef.current = drawData.discounts;
    }, [drawData.discounts]);
    // Terms modal states
    const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
    const [termsContent, setTermsContent] = useState<string>('');
    const [termsLoading, setTermsLoading] = useState<boolean>(false);
    const [termsEdited, setTermsEdited] = useState<boolean>(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string>('');

    const openTermsModal = async () => {
        try {
            setTermsLoading(true);
            setShowTermsModal(true);
            if (!termsEdited) {
                const { data } = await fetchTerms();
                setTermsContent(data?.html || '');
            }
        } catch (e) {
            setTermsContent('');
        } finally {
            setTermsLoading(false);
        }
    };
    const closeTermsModal = () => setShowTermsModal(false);
    const saveTermsModal = () => {
        setTermsEdited(true);
        setShowTermsModal(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            setImgLoading(true);
            // Check file size (1MB)
            if (selectedFile.size > 1 * 1024 * 1024) {
                setError('File size exceeds 1MB. Please select a smaller file.');
                console.error('File size exceeds 1MB. Please select a smaller file.');
                setValue('imageId', '', { shouldValidate: true });
                setImgLoading(false);
                return;
            }
            setError('');
            const formData = new FormData();
            formData.append('file', selectedFile);
            try {
                const response = await uploadImage(formData);
                const imageId = response?.data?.id;
                setValue('imageId', imageId, { shouldValidate: true });
                // Update current image URL
                if (imageId) {
                    setCurrentImageUrl(`${HOST_API}/public/download/${imageId}`);
                }
                setImgLoading(false);
            } catch (error) {
                setValue('imageId', '', { shouldValidate: true });
                console.error('Error uploading file:', error);
                setError(`Error uploading file: ${error}`);
                setImgLoading(false);
            }
        }
    };

    const handleRemoveImage = () => {
        setValue('imageId', undefined, { shouldValidate: true });
        setCurrentImageUrl('');
        // Clear the file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors, dirtyFields },
        watch,
        reset
    } = useForm<FormData>({
        resolver: zodResolver(validationSchema),
        defaultValues: {
            name: '',
            runAt: '',
            prize: '',
            entryCost: undefined,
            runMethod: '',
            imageId: undefined,
            timezone: 'Australia/Sydney',
            currency: 'AUD',
            ticketCap: undefined,
            emailWinner: true,
            numberOfEntries: 1,
            // Initialize discounts field so the first row inputs bind correctly
            discounts: [{ type: '', code: '' } as any]
        }
    });

    const entryCost = watch('entryCost');
    const currency = watch('currency');
    const [promoPeriods, setPromoPeriods] = useState<Array<{ start: string; end: string; multiplier: number }>>([]);
    const [promoPeriodsError, setPromoPeriodsError] = useState<string>('');
    const promoPeriodsRef = useRef<HTMLDivElement>(null);

    // Use server-provided ISO without transforming timezone to avoid shifting hours on reload
    const toInputFromServerIso = (iso?: string) => (iso ? String(iso).slice(0, 16) : '');

    // Load draw if in edit mode
    useEffect(() => {
        const load = async () => {
            if (!drawId) return;
            try {
                setInitialLoading(true);
                const res = await getDrawInfoById(drawId);
                const d = res.data;
                const formattedRunAt = toInputFromServerIso(d.runAt);
                reset({
                    name: d.name,
                    runAt: formattedRunAt,
                    prize: d.prize,
                    entryCost: Number(d.entryCost),
                    runMethod: d.runMethod,
                    imageId: d.imageId ?? undefined,
                    timezone: d.timezone,
                    currency: d.currency,
                    ticketCap: d.ticketCap ?? undefined,
                    numberOfEntries: (d.numberOfEntries !== undefined && d.numberOfEntries !== null)
                        ? Number(d.numberOfEntries)
                        : 1,
                    emailWinner: d.emailWinner !== false,
                    discounts: Array.isArray(d.discounts) && d.discounts.length > 0 ? d.discounts : ([{ type: '', code: '' }] as any)
                });
                const nowIso = new Date().toISOString();
                const expired = !!d.runAt && nowIso >= d.runAt;
                setIsLocked(((d.noOfPartic ?? 0) > 0));
                setIsInactive(!!d.deactivatedAt || expired);
                // preload discounts and terms UI state
                if (Array.isArray(d.discounts)) {
                    setDrawData(prev => ({ ...prev, discounts: d.discounts }));
                    discountsRef.current = d.discounts; // Initialize ref with existing discounts
                }
                // Populate links if backend returned them (association 'links')
                if (Array.isArray(d.links)) {
                    // Map server link shape to local shape and include per-link numberOfEntries when available
                    setLinks(d.links.map((l: any) => {
                        const perLinkEntries = (l.numberOfEntries ?? l.customTicketCount ?? l.effectiveNumberOfEntries);
                        return {
                            id: l.id,  // Include the ID so backend knows this is an existing link
                            name: l.name,
                            slug: l.slug,
                            entryCost: l.entryCost !== null && l.entryCost !== undefined ? Number(l.entryCost) : undefined,
                            numberOfEntries: perLinkEntries !== null && perLinkEntries !== undefined ? Number(perLinkEntries) : undefined
                        };
                    }));
                }
                if (Array.isArray(d.promoPeriods)) {
                    setPromoPeriods(
                        d.promoPeriods.map((p: any) => ({
                            start: p.start ? p.start.slice(0, 16) : '',
                            end: p.end ? p.end.slice(0, 16) : '',
                            multiplier: Number(p.multiplier) || 1
                        }))
                    );
                }
                if (d.termsHtml) {
                    setTermsContent(d.termsHtml);
                    setTermsEdited(true);
                }
                // No-op: runAt changes will be detected via react-hook-form dirtyFields
                // Set current image URL if exists
                if (d.imageId) {
                    setCurrentImageUrl(`${HOST_API}/public/download/${d.imageId}`);
                }
            } catch (e) {
                setError('Failed to load draw');
            } finally {
                setInitialLoading(false);
            }
        };
        load();
    }, [drawId, reset]);

    const validatePromoPeriodsLocal = () => {
        const isUpdate = !!drawId;
        const validation = validatePromoPeriods(promoPeriods, { 
            runAt: watch('runAt'),
            timezone: watch('timezone'),
            isUpdateMode: isUpdate,
            validateNotInPast: !isUpdate
        });
        
        if (!validation.isValid && validation.errorMessage) {
            setPromoPeriodsError(validation.errorMessage);
            // Scroll to promotional periods section
            promoPeriodsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }
        
        setPromoPeriodsError('');
        
        return true;
    };

    const onSubmit = async (data: FormData) => {
        try {
            setLoading(true);
            setError('');

            // Validate promotional periods before submission
            if (!validatePromoPeriodsLocal()) {
                setLoading(false);
                return;
            }

            // Build promo periods payload from state
            const nextPromo = (promoPeriods || [])
                .filter(p => p.start && p.end && Number(p.multiplier) >= 1)
                .map(p => ({ start: p.start, end: p.end, multiplier: Math.max(1, Math.floor(Number(p.multiplier))) }));

            // Get discounts from ref (most reliable)
            const combinedDiscounts = [...discountsRef.current];
            console.log('DEBUG: discountsRef.current:', discountsRef.current);
            console.log('DEBUG: drawData state at submit time:', drawData);
            console.log('DEBUG: Combined discounts for payload:', combinedDiscounts);
            
            // Only include pending discount if it's not empty and not already in the list
            const pendingType = watch('discounts.0.type');
            const pendingCode = watch('discounts.0.code');
            const pendingLabel = watch('discounts.0.label');
            
            if (pendingType && pendingCode && pendingType.trim() !== '' && pendingCode.trim() !== '') {
                // Check if this discount is not already in the list
                const isDuplicate = combinedDiscounts.some(discount => 
                    discount.type === pendingType && discount.code === pendingCode
                );
                
                if (!isDuplicate) {
                    combinedDiscounts.push({ 
                        type: String(pendingType), 
                        code: String(pendingCode),
                        label: pendingLabel ? String(pendingLabel) : undefined
                    });
                }
            }
            
            console.log('DEBUG: drawData.discounts:', drawData.discounts);
            console.log('DEBUG: combinedDiscounts:', combinedDiscounts);
            console.log('DEBUG: FINAL PAYLOAD DISCOUNTS:', combinedDiscounts);

            let payload: any;
            if (drawId && isLocked) {
                // When locked in edit mode, send promoPeriods AND drawLinks
                payload = { 
                    promoPeriods: nextPromo,
                    // Always include drawLinks even when locked so marketing links can be saved
                    drawLinks: links || []
                };
            } else {
                payload = {
                    ...data,
                    discounts: combinedDiscounts,
                    termsHtml: termsEdited ? termsContent : undefined,
                    promoPeriods: nextPromo
                };
                
                // Handle ticketCap: convert string to number and handle special cases
                                const ticketCapValue = data.ticketCap;
                                console.log('DEBUG: ticketCap raw value:', ticketCapValue, 'type:', typeof ticketCapValue);
                                
                                // Only compare to empty string when the value is actually a string
                                if (ticketCapValue === undefined || ticketCapValue === null || (typeof ticketCapValue === 'string' && ticketCapValue === '')) {
                                    delete payload.ticketCap;  // Omit field if empty
                                    console.log('DEBUG: ticketCap deleted (empty/undefined/null)');
                                } else {
                                    const numValue = typeof ticketCapValue === 'string' ? Number(ticketCapValue) : ticketCapValue;
                                    console.log('DEBUG: ticketCap converted to number:', numValue, 'isNaN:', Number.isNaN(numValue));
                                    
                                    if (Number.isNaN(numValue as any)) {
                                        delete payload.ticketCap;  // Omit if NaN
                                        console.log('DEBUG: ticketCap deleted (NaN)');
                                    } else if (numValue === 0) {
                                        payload.ticketCap = null;  // Backend expects null for unlimited
                                        console.log('DEBUG: ticketCap set to null (unlimited)');
                                    } else {
                                        payload.ticketCap = numValue as number;  // Send actual number
                                        console.log('DEBUG: ticketCap set to:', numValue);
                                    }
                                }
                
                console.log('DEBUG: FINAL PAYLOAD BEFORE SEND:', JSON.stringify(payload, null, 2));
                
                // Handle numberOfEntries - ensure it's a number
                if (payload.numberOfEntries) {
                    const numEntriesValue = typeof payload.numberOfEntries === 'string' 
                        ? Number(payload.numberOfEntries) 
                        : payload.numberOfEntries;
                    payload.numberOfEntries = Number.isNaN(numEntriesValue) ? 1 : numEntriesValue;
                }
                
                // Include any defined draw links so backend can persist them
                // Remove numberOfEntries from drawLinks - it should only be at the draw level
                if (Array.isArray(links) && links.length > 0) {
                    (payload as any).drawLinks = links.map(link => ({
                        id: link.id,
                        name: link.name,
                        slug: link.slug,
                        entryCost: link.entryCost,
                        numberOfEntries: link.numberOfEntries
                    }));
                }
                // Handle image removal: send null to remove image, or keep current value
                if (drawId) {
                    if (data.imageId === undefined) {
                        payload.imageId = null;
                    } else if (!data.imageId) {
                        delete payload.imageId;
                    }
                    // Preserve original runAt if user didn't change it
                    if (!(dirtyFields as any)?.runAt) {
                        delete payload.runAt;
                    }
                    // Preserve original timezone if user didn't change it
                    if (!(dirtyFields as any)?.timezone) {
                        delete payload.timezone;
                    }
                }
            }

            const response = drawId
                ? await updateDraw(drawId, payload)
                : await createDraw(payload);

            if (response && response.data) {
                // Check for link warnings
                if (response.data?.warning?.type === 'DRAW_LINKS_FAILED') {
                    setLinkWarning(response.data.warning.message);
                    // Still navigate but show warning for a few seconds
                    setTimeout(() => navigate(PATHS.WELCOME), 3000);
                } else {
                    navigate(PATHS.WELCOME);
                }
            } else {
                setError(drawId ? 'Failed to update draw. Please try again.' : 'Failed to create draw. Please try again.');
                setLoading(false);
            }
        } catch (error: any) {
            console.error('Detailed error:', error);
            const status = error?.response?.status;
            if (status === 409 && drawId) {
                // Don't show error for locked draws - user can still edit promotional periods and marketing links
                console.log('Draw is locked, but promotional periods and marketing links can still be saved');
            } else {
                const message = error?.response?.data?.error || (drawId ? 'Draw cannot be edited.' : 'An error occurred while creating the draw');
                setError(message);
            }
        } finally {
            setLoading(false);
        }
    };

    // Modify the add function to prevent form submission
    const handleAddDiscount = (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent form submission
        const type = getValues('discounts.0.type');
        const code = getValues('discounts.0.code');

        console.log('DEBUG: Adding discount - type:', type, 'code:', code);
        console.log('DEBUG: Current drawData before add:', drawData);

        if (type && code) {
            const label = getValues('discounts.0.label');
            const newDiscount = { type, code, label };
            discountsRef.current = [...discountsRef.current, newDiscount];
            
            setDrawData((prev) => {
                const newDiscounts = [...prev.discounts, newDiscount];
                return {
                    ...prev,
                    discounts: newDiscounts
                };
            });
            
            // Reset inputs
            setValue('discounts.0.type', '', { shouldValidate: true });
            setValue('discounts.0.code', '', { shouldValidate: true });
            setValue('discounts.0.label', '', { shouldValidate: true });
        }
    };

    const handleDeleteDiscount = (index: number) => {
        // Update ref
        discountsRef.current = discountsRef.current.filter((_, i) => i !== index);
        
        setDrawData((prev) => ({
            ...prev,
            discounts: prev.discounts.filter((_, i) => i !== index)
        }));
    };

    if (initialLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <span className="loading loading-ring loading-lg"></span>
            </div>
        );
    }

    return (
        <div className="flex items-start justify-center">
            {imgLoading && <LoadingOverlay />}
            <div className="w-full max-w-4xl shadow-xl py-10 px-6 rounded-2xl border border-border/30 bg-white">
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {isLocked && (
                        <div className="mb-4">
                            <Alert type="simple-outline" message={'This draw is locked due to tickets sold, payments in progress, or deactivation. Only Promotional periods and Marketing/Tracking links can be edited.'} />
                        </div>
                    )}
                    <div>
                        <label className="label">
                            <span className="label-text">
                                Draw Name{' '}
                                <Tooltip
                                    text="Give your draw a clear, descriptive name that helps participants understand what they're entering. This will be prominently displayed."
                                    direction="tooltip-left"
                                />
                            </span>
                        </label>

                        <input
                            type="text"
                            placeholder="Enter draw name"
                            className="input input-bordered w-full"
                            disabled={loading || isLocked || isInactive}
                            {...register('name')}
                        />
                        <label className="label">
                            {errors.name && (
                                <span className="label-text-alt text-red-600 font-semibold">
                                    {errors.name?.message}
                                </span>
                            )}
                        </label>
                    </div>

                    <div className="grid grid-cols-3">
                        <div className="col-span-2 mr-3">
                            <label className="label">
                                <span className="label-text text-sm">
                                    Draw Date & Time{' '}
                                    <Tooltip
                                        text="Set when the draw will take place. Choose a time that maximizes participation and gives enough time for promotion."
                                        direction="tooltip-left"
                                    />
                                </span>
                            </label>
                            <input
                                type="datetime-local"
                                className="input input-bordered  w-full"
                                disabled={loading || isLocked || isInactive}
                                min={today}
                                {...register('runAt')}
                            />
                            <label className="label">
                                {errors.runAt && (
                                    <span className="label-text-alt text-red-600 font-semibold">
                                        {errors.runAt?.message}
                                    </span>
                                )}
                            </label>
                        </div>
                        <div>
                            <label className="label">
                                <span className="label-text text-sm">Timezone</span>
                            </label>
                            <select
                                className="select select-bordered  w-full"
                                disabled={loading || isLocked || isInactive}
                                {...register('timezone')}>
                                <option value="" disabled>
                                    Choose Timezone
                                </option>
                                {timeZone.map((option, index) => (
                                    <option key={index} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            <label className="label">
                                {errors.timezone && (
                                    <span className="label-text-alt text-red-600 font-semibold">
                                        {errors.timezone?.message}
                                    </span>
                                )}
                            </label>

                        </div>
                    </div>
                    <div>
                        {/* Promotional periods */}
                        <div className="mt-4 mb-4" ref={promoPeriodsRef}>
                            <div className="flex items-center justify-between">
                                <label className="label">
                                    <span className="label-text">Promotional periods{' '}
                                        <Tooltip
                                            text="Boost entries for a set period. e.g. ‘Every ticket = 2 entries’ or ‘Every $1 = 2 entries."
                                            direction="tooltip-left"
                                        />
                                    </span>
                                </label>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-2 rounded-btn bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                                    disabled={loading || isInactive}
                                    onClick={() => {
                                        const runAtVal = watch('runAt');
                                        const timezone = watch('timezone');
                                        // const nowIso = getNowInTimezone(timezone);
                                        // Default start 15 minutes from now to avoid immediate past on submit
                                        const nowPlus15 = (() => {
                                            const d = new Date();
                                            d.setMinutes(d.getMinutes() + 15);
                                            return new Intl.DateTimeFormat('sv-SE', {
                                                timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }).format(d).replace(' ', 'T');
                                        })();
                                        const existing = (promoPeriods || []).filter(p => p.start && p.end);
                                        const lastEnd = existing.length
                                            ? existing
                                                .slice()
                                                .sort((a, b) => (a.start || '').localeCompare(b.start || ''))
                                                .at(-1)!.end
                                            : '';
                                        const start = lastEnd && lastEnd > nowPlus15 ? lastEnd : nowPlus15;
                                        // If draw date exists and is before start, clamp end to start; else use draw date
                                        const end = runAtVal && runAtVal > start ? runAtVal : start;
                                        const newPeriod = { start, end, multiplier: 1 };
                                        setPromoPeriods([...(promoPeriods || []), newPeriod]);
                                    }}
                                >
                                    <FaPlus className="w-4 h-4 mr-1" />
                                    Add period
                                </button>
                            </div>
                            {(promoPeriods || []).map((p, idx) => {
                                const runAtVal = watch('runAt');
                                const timezone = watch('timezone');
                                const nowIso = getNowInTimezone(timezone);
                                const startInvalid = !!p.start && !!p.end && p.start >= p.end;
                                const afterRun = !!runAtVal && (!!p.end && p.end > runAtVal);

                                // Live overlap guards: compute adjacent bounds based on sorted periods
                                const sorted = (promoPeriods || [])
                                    .map((pp, i) => ({ i, s: pp.start || '' }))
                                    .sort((a, b) => a.s.localeCompare(b.s));
                                const pos = sorted.findIndex(x => x.i === idx);
                                const prevEnd = pos > 0 ? (promoPeriods[sorted[pos - 1].i]?.end || '') : '';
                                const nextStart = pos >= 0 && pos < sorted.length - 1 ? (promoPeriods[sorted[pos + 1].i]?.start || '') : '';
                                const startMinBase = prevEnd && prevEnd > nowIso ? prevEnd : nowIso;
                                // Bump min by +1 minute to reduce race with current time on slow interactions
                                const startMin = (() => {
                                    try {
                                        const d = new Date(startMinBase);
                                        d.setMinutes(d.getMinutes() + 1);
                                        return d.toISOString().slice(0, 16);
                                    } catch {
                                        return startMinBase;
                                    }
                                })();
                                const startMax = nextStart || runAtVal || undefined;
                                const endMin = p.start || startMin;
                                const endMax = nextStart || runAtVal || undefined;

                                return (
                                    <div key={idx} className="mt-2 p-3 border border-gray-200 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                            <div className="md:col-span-4">
                                                <label className="label"><span className="label-text text-sm">Start</span></label>
                                                <input
                                                    type="datetime-local"
                                                    className="input input-bordered w-full"
                                                    disabled={loading || isInactive}
                                                    value={p.start}
                                                    min={drawId ? undefined : startMin}
                                                    max={startMax}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        const copy = [...promoPeriods];
                                                        copy[idx] = { ...copy[idx], start: v };
                                                        setPromoPeriods(copy);
                                                    }}
                                                />
                                            </div>
                                            <div className="md:col-span-4">
                                                <label className="label"><span className="label-text text-sm">End</span></label>
                                                <input
                                                    type="datetime-local"
                                                    className="input input-bordered w-full"
                                                    disabled={loading || isInactive}
                                                    value={p.end}
                                                    min={endMin}
                                                    max={endMax}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        const copy = [...promoPeriods];
                                                        copy[idx] = { ...copy[idx], end: v };
                                                        setPromoPeriods(copy);
                                                    }}
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="label"><span className="label-text text-sm">Multiplier</span></label>
                                                <input
                                                    type="number"
                                                    className="input input-bordered w-full"
                                                    min={1}
                                                    disabled={loading || isInactive}
                                                    value={p.multiplier}
                                                    onChange={(e) => {
                                                        const v = Math.max(1, Math.floor(Number(e.target.value || 1)));
                                                        const copy = [...promoPeriods];
                                                        copy[idx] = { ...copy[idx], multiplier: v };
                                                        setPromoPeriods(copy);
                                                    }}
                                                />
                                            </div>
                                            <div className="md:col-span-2 flex items-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline btn-error w-full"
                                                    disabled={loading || isInactive}
                                                    onClick={() => setPromoPeriods(promoPeriods.filter((_, i) => i !== idx))}
                                                    aria-label={`Remove period ${idx + 1}`}
                                                >
                                                    <span className="md:hidden">Remove</span>
                                                    <span className="hidden md:flex items-center justify-center">
                                                        <FaXmark className="w-4 h-4 lg:mr-2" />
                                                        <span className="hidden lg:inline">Remove</span>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                        {(startInvalid || afterRun) && (
                                            <div className="mt-2">
                                                <span className="text-xs text-red-600">
                                                    {startInvalid ? 'Start must be before End.' : 'End must be before draw date.'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {promoPeriodsError && (
                            <label className="label">
                                <span className="label-text-alt text-red-600 font-semibold">
                                    {promoPeriodsError}
                                </span>
                            </label>
                        )}

                    </div>
                    <div>
                        <label className="label">
                            <span className="label-text text-sm">
                                Prize{' '}
                                <Tooltip
                                    text="Describe the prize in detail, including its value and any specific conditions. Be clear and transparent about what winners will receive."
                                    direction="tooltip-left"
                                />
                            </span>
                        </label>
                        <textarea
                            placeholder="Enter draw prize"
                            className="textarea input-bordered  w-full"
                            disabled={loading || isLocked || isInactive}
                            {...register('prize')}
                        />
                        <label className="label">
                            {errors.prize && (
                                <span className="label-text-alt font-semibold text-red-600">
                                    {errors.prize.message}
                                </span>
                            )}
                        </label>
                    </div>
                    <div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Entry Cost{' '}
                                        <Tooltip
                                            text="Set how much it costs to enter the draw. Consider your target audience and the prize value when setting this amount. This directly affects your potential earnings."
                                            direction="tooltip-left"
                                        />
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    placeholder="Enter entry cost"
                                    step="any"
                                    className="input input-bordered w-full"
                                    disabled={loading || isLocked || isInactive}
                                    {...register('entryCost', { valueAsNumber: true })}
                                    min={1}
                                />
                                <label className="label">
                                    {errors.entryCost && (
                                        <span className="label-text-alt text-red-600 font-semibold">
                                            {errors.entryCost?.message}
                                        </span>
                                    )}
                                </label>
                            </div>

                            <div className="col-span-1">
                                <label className="label">
                                    <span className="label-text text-sm">Currency</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    disabled={loading || isLocked || isInactive}
                                    defaultValue="AUD"
                                    {...register('currency')}>
                                    <option value="" disabled>
                                        Choose currency
                                    </option>
                                    {currencyOptions.map((option: string, index: number) => (
                                        <option key={index} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                                <label className="label">
                                    {errors.currency && (
                                        <span className="label-text-alt text-red-600 font-semibold">
                                            {errors.currency?.message}
                                        </span>
                                    )}
                                </label>
                            </div>
                        </div>

                        {/* Earnings calculator below both fields */}
                        {entryCost > 0 && (
                            <div className="mt-2">
                                <Alert
                                    type="simple-outline"
                                    message={`If only 10% of your 100k followers enter your draw:
Your potential earnings would be ${currency} ${calculateEarnings(entryCost).netEarnings} (after 15% platform fee) from a total revenue of ${currency} ${calculateEarnings(entryCost).grossRevenue}`}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="label">
                            <span className="label-text text-sm">
                                Draw Type{' '}
                                <Tooltip
                                    text="Choose how winners will be selected. This determines the draw mechanism and affects how participants are chosen."
                                    direction="tooltip-left"
                                />
                            </span>
                        </label>
                        <select
                            className="select select-bordered  w-full"
                            disabled={loading || isLocked || isInactive}
                            {...register('runMethod')}>
                            <option value="" disabled>
                                Choose Draw Type
                            </option>
                            {drawType.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {option.option}
                                </option>
                            ))}
                        </select>
                        <label className="label">
                            {errors.runMethod && (
                                <span className="label-text-alt text-red-600 font-semibold">
                                    {errors.runMethod?.message}
                                </span>
                            )}
                        </label>

                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-1">
                            <label className="label">
                                <span className="label-text text-sm">
                                    Max Number of Ticket(s){' '}
                                    <Tooltip
                                        text="Maximum number of tickets that can be sold for this draw. Set to 0 for unlimited tickets."
                                        direction="tooltip-right"
                                    />
                                </span>
                            </label>
                            <input
                                type="number"
                                placeholder="0 = unlimited"
                                className="input input-bordered w-full"
                                disabled={loading || isLocked || isInactive}
                                {...register('ticketCap')}
                                min={0}
                            />
                            <label className="label">
                                {errors.ticketCap && (
                                    <span className="label-text-alt text-red-600 font-semibold">
                                        {errors.ticketCap?.message}
                                    </span>
                                )}
                            </label>
                        </div>

                        <div className="col-span-1">
                            <label className="label">
                                <span className="label-text text-sm">
                                    Number of Entries{' '}
                                    <Tooltip
                                        text="How many tickets each customer receives per purchase. For example, if set to 3, buying 1 ticket gives the customer 3 entries (before promotional multipliers)."
                                        direction="tooltip-right"
                                    />
                                </span>
                            </label>
                            <input
                                type="number"
                                placeholder="1"
                                className="input input-bordered w-full"
                                disabled={loading || isLocked || isInactive}
                                {...register('numberOfEntries', { valueAsNumber: true })}
                                min={1}
                            />
                            <label className="label">
                                {errors.numberOfEntries && (
                                    <span className="label-text-alt text-red-600 font-semibold">
                                        {errors.numberOfEntries?.message}
                                    </span>
                                )}
                            </label>
                        </div>

                        <div className="col-span-1"></div>
                    </div>

                    {/* Divider above */}
                    <div className="primary-divider"></div>

                    {/* Heading for discount section */}
                    <div className="flex items-center justify-between text-base font-bold text-left mb-5">
                        <span>Exclusive Discounts and Perks</span>

                    </div>

                    <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-2">
                            <label className="label h-7 mb-0">
                                <span className="label-text text-sm">
                                    Type
                                    <Tooltip
                                        text="Choose what kind of special offer to provide. Strategic discounts can help boost participation and reward specific groups."
                                        direction="tooltip-left"
                                    />
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full h-9 min-h-[36px] text-sm"
                                disabled={loading || isLocked || isInactive}
                                {...register('discounts.0.type')}>
                                <option value="">Select</option>
                                {discountTypes.map((type, index) => (
                                    <option key={index} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-span-5">
                            <label className="label h-7 mb-0">
                                <span className="label-text text-sm">
                                    {watch('discounts.0.type') === 'affiliate' ? 'Affiliate Link' : 'Discount Code'}
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder={watch('discounts.0.type') === 'affiliate' ? 'Enter a valid URL' : 'Enter discount code'}
                                className="w-full input input-bordered flex-1 h-9 min-h-[36px] text-sm"
                                disabled={!watch('discounts.0.type') || loading || isLocked}
                                {...register('discounts.0.code', {
                                    validate: value => {
                                        if (watch('discounts.0.type') === 'affiliate' && value) {
                                            try {
                                                new URL(value);
                                                return true;
                                            } catch (e) {
                                                return 'Please enter a valid URL for affiliate link';
                                            }
                                        }
                                        return true;
                                    }
                                })}
                            />
                        </div>
                        
                        <div className="col-span-3">
                            <label className="label h-7 mb-0">
                                <span className="label-text text-sm">Label (Optional)</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. Summer Sale"
                                className="w-full input input-bordered flex-1 h-9 min-h-[36px] text-sm"
                                disabled={!watch('discounts.0.type') || loading || isLocked}
                                {...register('discounts.0.label', { required: false })}
                            />
                        </div>
                        
                        <div className="col-span-2 flex items-end">
                            <button
                                type="button"
                                onClick={handleAddDiscount}
                                className={`btn w-full h-9 min-h-[36px] ${watch('discounts.0.type') && watch('discounts.0.code')
                                    ? 'btn-primary'
                                    : 'btn-disabled'
                                    }`}
                                disabled={!watch('discounts.0.type') || !watch('discounts.0.code') || loading || isLocked}>
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Display added discount codes */}
                    <div className="mt-4 space-y-2">
                        {drawData.discounts.map((item, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg group hover:bg-gray-100 transition-colors duration-200">
                                <div className="flex flex-wrap items-center gap-2 flex-1">
                                    {item.label && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {item.label}
                                        </span>
                                    )}
                                    <span className="text-sm font-medium capitalize">
                                        {item.type}:
                                    </span>
                                    {item.type === 'affiliate' ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600 break-all">{item.code}</span>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    navigator.clipboard.writeText(item.code);
                                                    // Add a visual feedback
                                                    alert('Link copied to clipboard!');
                                                }}
                                                className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none"
                                                aria-label="Copy affiliate link">
                                                Copy
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-sm">{item.code}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteDiscount(index);
                                    }}
                                    className="text-gray-400 hover:text-red-500 focus:outline-none ml-2"
                                    aria-label="Delete discount">
                                    <span className="text-lg font-bold">&times;</span>
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* ...existing code... */}
                    {/* Move marketing/tracking links section above draw image */}
                    {/* ...existing code... */}
                    <div className="primary-divider"></div>
                    {/* ...existing code... */}
                    <div className="mb-10 mt-10" id="marketing-links">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold">Marketing / Tracking Links</h2>
                            <span className="text-xs text-gray-500">Optional – create unique purchase tracking links</span>
                        </div>
                        <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">

                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                                <div className="md:col-span-2">
                                    <label className="label"><span className="label-text text-xs">Link Name</span></label>
                                    <input 
                                        type="text" 
                                        className="input input-bordered w-full" 
                                        placeholder="Link Name" 
                                        value={linkForm?.name ?? ''} 
                                        disabled={loading}
                                        onChange={e => setLinkForm(f => ({ ...f, name: e.target.value }))} 
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="label"><span className="label-text text-xs">Slug</span></label>
                                    <input 
                                        type="text" 
                                        className={`input input-bordered w-full ${
                                            linkForm?.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(linkForm.slug) 
                                                ? 'input-error' 
                                                : ''
                                        }`}
                                        placeholder="e.g. summer-sale" 
                                        value={linkForm?.slug ?? ''} 
                                        disabled={loading}
                                        onChange={e => {
                                            let cleanSlug = e.target.value
                                                .trim()
                                                .toLowerCase()
                                                .replace(/[^a-z0-9-]/g, '-')  // Replace invalid chars with hyphen
                                                .replace(/-+/g, '-')          // Replace multiple hyphens with single
                                                .replace(/^-|-$/g, '');       // Remove leading/trailing hyphens
                                            
                                            // Ensure slug is not empty and matches backend regex
                                            if (cleanSlug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(linkForm.slug ?? '')) {
                                                // If still invalid, try to fix by ensuring it starts/ends with alphanumeric
                                                cleanSlug = cleanSlug.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
                                            }
                                            
                                            setLinkForm(f => ({ ...f, slug: cleanSlug }));
                                        }} 
                                    />
                                    {linkForm?.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(linkForm.slug) && (
                                        <div className="text-xs text-red-600 mt-1">
                                            Slug must contain only lowercase letters, numbers, and hyphens
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="label"><span className="label-text text-xs">Entry Cost <Tooltip text="Leave empty for default to use draw entry cost" direction="tooltip-left" /></span></label>
                                    <input 
                                        type="number" 
                                        className="input input-bordered w-full" 
                                        value={linkForm?.entryCost ?? ''} 
                                        disabled={loading}
                                        onChange={e => setLinkForm(f => ({ ...f, entryCost: e.target.value ? Number(e.target.value) : undefined }))} 
                                    />
                                </div>
                                <div>
                                    <label className="label"><span className="label-text text-xs">Number of Entries <Tooltip text="Leave empty for default to use draw number of entries" direction="tooltip-left" /></span></label>
                                    <input 
                                        type="number" 
                                        className="input input-bordered w-full" 
                                        value={linkForm?.numberOfEntries ?? ''} 
                                        min={1}
                                        disabled={loading}
                                        onChange={e => setLinkForm(f => ({ ...f, numberOfEntries: e.target.value ? Number(e.target.value) : undefined }))} 
                                    />
                                </div>
                            </div>
                            <button 
                                type="button" 
                                className="inline-flex items-center gap-2 rounded-btn bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleAddLink();
                                }} 
                                disabled={
                                    loading ||
                                    !linkForm?.name || 
                                    !linkForm?.slug || 
                                    !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(linkForm.slug) ||
                                    links?.some(link => link.slug === linkForm.slug)
                                }
                            >
                                <FaPlus className="w-4 h-4 mr-1" />
                                Add Link
                            </button>
                            <div className="mt-4">
                                {links?.length === 0 ? (
                                    <p className="text-sm text-gray-500">No links added yet. Add one above.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {links.map((link, idx) => (
                                            <li key={idx} className="flex items-start gap-3 bg-white border border-gray-200 px-4 py-3 rounded-lg">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-medium text-sm">{link.name}</span>
                                                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{link.slug}</code>
                                                    </div>
                                                    <div className="mt-1 text-[11px] text-gray-500 break-all">URL: /draw-info/{drawId || '{drawId}'}/{link.slug}</div>
                                                    <div className="mt-1 text-xs text-gray-600">Price: {link.entryCost !== undefined ? link.entryCost : entryCost || '-'}</div>
                                                    <div className="mt-1 text-xs text-gray-600">Entries: {link.numberOfEntries !== undefined ? link.numberOfEntries : (getValues('numberOfEntries') || 1)}</div>
                                                </div>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-outline btn-error btn-xs" 
                                                    disabled={loading}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleRemoveLink(idx);
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* ...existing code... */}
                    <div>
                        <label className="label">
                            <span className="label-text text-sm">
                                Draw Image{' '}
                                <Tooltip
                                    text="Upload an eye-catching image that represents your draw. High-quality visuals can increase participation rates."
                                    direction="tooltip-left"
                                />
                            </span>
                        </label>
                        <input
                            type="file"
                            className="file-input file:bg-primary/10 file:text-primary file:border-none file-input-bordered w-full"
                            accept=".jpeg, .jpg, .png"
                            onChange={handleFileChange}
                            disabled={loading || isLocked || isInactive}
                        />
                        {currentImageUrl && (
                            <div className="mt-4">
                                <div className="relative inline-block">
                                    <img
                                        src={currentImageUrl}
                                        alt="Current draw image"
                                        className="w-full max-w-xs h-32 object-cover rounded-lg border border-gray-200"
                                    />
                                    {!isLocked && (
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full shadow-lg"
                                            title="Remove image"
                                        >
                                            <FaTrash className="text-xs" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="label">
                            <span className="label-text-alt">
                                jpeg, pdf or jpg files only. Max size: 1MB
                            </span>
                        </div>
                        {error && (
                            <div className="label">
                                <span className="label-text-alt text-red-600 font-semibold">
                                    {error}
                                </span>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text text-sm">
                                Email Winner{' '}
                                <Tooltip
                                    text="Choose whether to automatically email the winner when the draw is completed."
                                    direction="tooltip-left"
                                />
                            </span>
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                className="toggle toggle-primary"
                                checked={watch('emailWinner') !== false}
                                onChange={(e) => setValue('emailWinner', e.target.checked)}
                                disabled={loading || isLocked || isInactive}
                            />
                            <span className="text-sm text-gray-600">
                                Automatically email the winner when draw is completed
                            </span>
                        </div>
                    </div>

                    <div className="mt-4 mb-5 space-y-4">
                        <Alert
                            message={
                                'You can edit this draw until the first ticket is sold or a payment is in progress. After that, only cancellation is allowed.'
                            }
                            type="simple-outline"
                        />
                        <Alert
                            message={
                                'If your prize money is over $30,000 please contact us at hello@vivaclub.io to arrange your trade promotional license.'
                            }
                            type="simple-outline"
                        />

                        {/* Terms block */}
                        <div className="rounded-xl border border-gray-200 bg-white p-5">
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 rounded-xl bg-purple-100 text-purple-600 p-3">
                                    <FaRegFileLines className="text-lg" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-left">Draw Terms &amp; Conditions</p>
                                    <p className="mt-1 text-sm text-gray-600 text-left">
                                        The default VivaClub terms will be applied to your draw. You can edit them here to add or remove any clauses specific to this draw.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 text-left">
                                <button
                                    type="button"
                                    onClick={openTermsModal}
                                    className="inline-flex items-center gap-2 rounded-btn bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                                    disabled={isLocked}
                                >
                                    <FaPen /> Edit Terms
                                </button>
                            </div>
                            <div className="mt-4 flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2">
                                <FaTriangleExclamation className="mt-0.5 text-yellow-600" />
                                <p className="text-xs text-gray-700 text-left">
                                    Once published, terms cannot be edited—only canceled.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Add error display */}
                    {error && (
                        <div className="alert alert-error mb-4">
                            <span>{error}</span>
                        </div>
                    )}
                    {linkWarning && (
                        <div className="alert alert-warning mb-4">
                            <span>{linkWarning}</span>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-primary w-full rounded-btn font-normal mt-4 bottom-0"
                        disabled={loading || isInactive}>
                        {loading ? (
                            <span className="loading loading-dots loading-md items-center"></span>
                        ) : (
                            drawId ? 'Update Draw' : 'Publish'
                        )}
                    </button>
                </form>
            </div>

            {/* Terms & Conditions Modal */}
            {showTermsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-hidden">
                    <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl p-4 max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between mb-2 flex-shrink-0">
                            <h3 className="text-lg font-semibold">{termsEdited ? 'Edit Terms & Conditions (this draw)' : 'New Terms & Conditions (this draw)'}</h3>
                            <button onClick={closeTermsModal} className="btn btn-sm">✕</button>
                        </div>
                        <div className="border rounded-lg flex-1 overflow-y-scroll">
                            {termsLoading ? (
                                <div className="flex items-center justify-center h-full"><span className="loading loading-dots loading-md"></span></div>
                            ) : (
                                <div className="h-full overflow-hidden">
                                    <ReactQuill
                                        theme="snow"
                                        value={termsContent}
                                        onChange={setTermsContent}
                                        style={{ height: '100%' }}
                                        modules={{
                                            toolbar: [
                                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                [{ 'color': [] }, { 'background': [] }],
                                                [{ 'align': [] }],
                                                ['link', 'blockquote', 'code-block'],
                                                ['clean']
                                            ],
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 mt-3 flex-shrink-0">
                            <button className="btn" onClick={closeTermsModal}>Cancel</button>
                            <button className="btn btn-primary" onClick={saveTermsModal}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateDraw;
